import { c as _c } from "react/compiler-runtime";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/config/react-query";
import type { RoomCreate, RoomDetail, RoomUpdate } from "@/types/room";
import { createRoom, deleteRoom, listRooms, updateRoom } from "./room";
import { invalidateByKey, revertOnError, startOptimisticChange } from "@/api/utils/query-helpers";
export const roomQueryKeys = {
  all: ["provider", "admin", "room"] as const,
  list: () => [...roomQueryKeys.all, "list"] as const
};
export const roomQueryOptions = {
  list: () => ({
    queryKey: roomQueryKeys.list(),
    queryFn: () => listRooms(),
    select: (data: RoomDetail[] | null) => data ?? []
  })
};
export const roomMutations = {
  create: async (room: RoomCreate) => await createRoom(room),
  update: async (room: RoomUpdate) => await updateRoom(room),
  delete: async (roomId: string) => await deleteRoom(roomId)
};
function sortRooms(items: RoomDetail[]): RoomDetail[] {
  return [...items].sort((a, b) => {
    const ad = a.createdDate ?? "";
    const bd = b.createdDate ?? "";
    if (ad !== bd) return ad < bd ? -1 : 1;
    return a.id.localeCompare(b.id);
  });
}
export const useUpsertRoomMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp,
      onMutate: _temp2,
      onError: _temp3,
      onSuccess: _temp4,
      onSettled: _temp5
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useDeleteRoomMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp6,
      onMutate: _temp7,
      onError: _temp8,
      onSettled: _temp9
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
async function _temp(t0) {
  const {
    data
  } = t0;
  if ("id" in data && data.id) {
    return await updateRoom(data as RoomUpdate);
  }
  return await createRoom(data as RoomCreate);
}
async function _temp2(t0) {
  const {
    data: data_0
  } = t0;
  const key = roomQueryKeys.list();
  const isUpdate = "id" in data_0 && data_0.id;
  const tempId = isUpdate ? (data_0 as RoomUpdate).id : `tmp-rom-${Date.now()}`;
  const optimisticData = {
    ...data_0,
    id: tempId
  };
  const {
    previous
  } = await startOptimisticChange(key, () => {
    const current = queryClient.getQueryData(key);
    const index = current?.findIndex(r => r.id === optimisticData.id) ?? -1;
    if (current && index >= 0) {
      const updated = {
        ...current[index],
        ...optimisticData
      } as RoomDetail;
      const next = sortRooms([...current.slice(0, index), updated, ...current.slice(index + 1)]);
      queryClient.setQueryData(key, next);
    } else {
      const optimistic = {
        id: optimisticData.id,
        name: optimisticData.name ?? optimisticData.id,
        color: optimisticData.color ?? null,
        locationId: (optimisticData as RoomCreate).locationId
      };
      const next_0 = sortRooms([...(current ?? []), optimistic]);
      queryClient.setQueryData(key, next_0);
    }
  });
  return {
    key,
    previous,
    tempId
  };
}
function _temp3(_err, _vars, ctx) {
  if (ctx) {
    revertOnError(ctx.key, ctx.previous);
  }
}
function _temp4(server, _vars_0, ctx_0) {
  if (!server || !ctx_0?.tempId) {
    return;
  }
  const key_0 = roomQueryKeys.list();
  const current_0 = queryClient.getQueryData(key_0);
  if (!current_0) {
    return;
  }
  const idx = current_0.findIndex(r_0 => r_0.id === ctx_0.tempId);
  if (idx >= 0) {
    const next_1 = [...current_0];
    next_1[idx] = {
      ...next_1[idx],
      ...server
    } as RoomDetail;
    queryClient.setQueryData(key_0, sortRooms(next_1));
  }
}
function _temp5() {
  return invalidateByKey(roomQueryKeys.list());
}
async function _temp6(t0) {
  const {
    id
  } = t0;
  return await deleteRoom(id);
}
async function _temp7(t0) {
  const {
    id: id_0
  } = t0;
  const key = roomQueryKeys.list();
  const {
    previous
  } = await startOptimisticChange(key, () => {
    const current = queryClient.getQueryData(key);
    if (!current) {
      return;
    }
    queryClient.setQueryData(key, current.filter(r => r.id !== id_0));
  });
  return {
    key,
    previous
  };
}
function _temp8(_err, _vars, ctx) {
  if (ctx) {
    revertOnError(ctx.key, ctx.previous);
  }
}
function _temp9() {
  return invalidateByKey(roomQueryKeys.list());
}